/**
 * Post-login redirect preservation (MS-2454). Uses sessionStorage, not router
 * state: the email flow full-reloads and SSO round-trips through the IdP —
 * neither keeps in-memory state, but same-tab sessionStorage survives both.
 */

const REDIRECT_KEY = "midship:postLoginRedirect";

// Never redirect back to these — it would loop into auth or an interstitial.
const AUTH_PATHS = [
  "/signin",
  "/sso/callback",
  "/auth/optrooauth/callback",
  "/reset-password",
  "/referral",
  "/accept-terms",
  "/subscription-inactive",
];

/**
 * Open-redirect guard. Resolves `candidate` against the current origin with the
 * URL parser (the same one the browser navigates with), so protocol-relative
 * (`//evil.com`), absolute (`https://evil.com`), backslash (`/\evil.com`), and
 * `javascript:`/`data:` inputs all read as a foreign origin and are rejected.
 * Returns only an internal `path + query + hash`, else `fallback`.
 */
export function safeInternalPath(
  candidate: string | null | undefined,
  fallback = "/audit",
): string {
  if (typeof candidate !== "string" || candidate.length === 0) {
    return fallback;
  }
  try {
    const url = new URL(candidate, window.location.origin);
    if (url.origin !== window.location.origin) {
      return fallback;
    }
    // Reject a protocol-relative path (leading // or /\): when navigated it
    // escapes to another origin (//evil.com -> https://evil.com). Unreachable
    // via the current callers (they pass a path, not a full URL) but hardens
    // the guard against any caller that passes an absolute same-origin URL.
    if (/^\/[/\\]/.test(url.pathname)) {
      return fallback;
    }
    const isAuthPath = AUTH_PATHS.some(
      (authPath) =>
        url.pathname === authPath || url.pathname.startsWith(`${authPath}/`),
    );
    if (isAuthPath) {
      return fallback;
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
}

/**
 * Remember where the user was headed before bouncing them to sign-in.
 * No-ops for empty, foreign, or auth-route inputs.
 */
export function stashPostLoginRedirect(
  candidate: string | null | undefined,
): void {
  const safe = safeInternalPath(candidate, "");
  if (!safe) return;
  try {
    sessionStorage.setItem(REDIRECT_KEY, safe);
  } catch (err) {
    // setItem can throw in private mode or when storage is full; harmless —
    // the user just lands on the default page instead of their deep link.
    console.error("Failed to stash post-login redirect", err);
  }
}

/**
 * Read and clear the stashed redirect, returning a validated in-app path or
 * `fallback` when nothing valid was stored.
 */
export function consumePostLoginRedirect(fallback = "/audit"): string {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(REDIRECT_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
  } catch (err) {
    console.error("Failed to read post-login redirect", err);
    return fallback;
  }
  return safeInternalPath(raw, fallback);
}
